﻿import { QueryKey, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TablePaginationConfig } from "antd/es/table";
import { FilterValue, SorterResult } from "antd/es/table/interface";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { FormInstance } from "antd/es/form";
import { toJson } from "../helpers/apiHelpers";
import { ApiError, fetchEGate } from "../helpers/eGateApi";
import { apiErrorsToFieldErrors, joinIfArray, qsTypeDecoder } from "../helpers/utils";
import qs from "qs";
import { useHotkeys } from "react-hotkeys-hook";
import { PartialListDto } from "../api/types";
import useApp from "./useApp";
import { ReactNode, startTransition } from "react";
import { isValueUsed as baseIsValueUsed } from "../helpers/api/UniquenesApi";
import { Modal, notification } from "antd";
import { useLocation, useParams } from "react-router";
import { TFunction } from "i18next";
import { responseErrorNotification } from "../helpers/responseErrorNotification";
import { trackError, trackEvent } from "../helpers/analytics";
import unionBy from "lodash-es/unionBy";
import isArray from "lodash-es/isArray";
import { useDebounce } from "use-debounce";
import { IdType } from "../env";
import { queryKeyMatchesEntity, queryKeyMatchesOperations } from "./entityQueryKeys";
import type { DetailOf, EntityOperations, FilterOf, ItemOf, ListOf, PageOf } from "../api/entity";
import { resolveOperations } from "../api/entity";
import { silent, unwrap } from "../api";
import useFormDirty from "./useFormDirty";

export interface EntityConfigBase {
  baseKey: string;
  key: string;
  keyDetail?: string;
  baseRoute: string;
}

/**
 * Entity configuration
 */
export interface EntityConfig<TFilter = any> extends EntityConfigBase {
  /**
   * Default API version to use for all API calls for this entity
   */
  apiVersion?: string;
  /**
   * Base endpoint for API calls - other endpoints are derived from this
   */
  endpoint?: string;
  /**
   * The generated SDK functions for this entity's slots, from `src/api/operations`.
   *
   * When present, `useEntity` calls the named operation instead of concatenating
   * `endpoint` into a URL and going through `fetchEGate`. Absent — or overridden by
   * `getDetailEndpoint` / `listEndpoint` / `customEndpoint`, which are runtime strings
   * no named function can express — it falls back to the old path.
   */
  operations?: EntityOperations;
  /**
   * Endpoint for list API calls - special case for overriding the /list endpoint
   */
  listEndpoint?: string;
  /**
   * Endpoint for detail API calls - special case for overriding the detail endpoint
   */
  getDetailEndpoint?: (id: IdType) => string;
  /**
   * Endpoint for list API calls - special case for overriding the whole endpoint
   */
  getEndpoint?: () => string;
  /**
   * Initial data to use for react-query
   */
  initialData?: unknown;
  /**
   * Initial filter to be synced with the URL
   */
  initialFilter?: TFilter;
  /**
   * Singular name of the entity
   */
  singularName: (t: TFunction) => string;
  /**
   * Name of the entity - pluralized (used as the name of the page and in the menu)
   */
  name: (t: TFunction) => string;
  /**
   * Roles that can view the page
   */
  roleView: string[];
  /**
   * Roles that can edit or delete entity
   */
  roleAdmin: string[];
  /**
   * Indicates that the ID should not be included in the client URL - for pages without ID in URL such as /profile
   */
  noIdInUrl?: boolean;
  /**
   * Indicates that the ID should not be included in the API call - for pages without ID in URL such as /profile
   */
  noIdInCall?: boolean;
  /**
   * Indicates that the page should not be redirected to the detail page after edit or create
   */
  noRedirect?: boolean;
  /**
   * Formats the filter before calling the API - example: converts local time to UTC
   */
  filterFormatter?: (filter: TFilter) => TFilter;
  /**
   * Can be used to disable fetching of the data based on the filter
   */
  shouldFetch?: (filter: TFilter) => boolean;
  /**
   * Icon to show in menu
   */
  icon?: ReactNode;
  /**
   * Icon to show in activate button
   */
  activateIcon?: ReactNode;
  /**
   * Text to show in activate button
   */
  activateText?: ReactNode;
  /**
   * Send culture to API
   */
  sendCulture?: boolean;
  /**
   * Disable cache
   */
  disableCache?: boolean;
  /**
   * Dependent entities - used for invalidation (for example when we blacklist a key we need to invalidate the list of blacklisted keys)
   */
  dependencies?: QueryKey; //for invalidation
  /**
   * Transforms the raw data from the API before it is saved in react-query
   */
  normalizer?: (raw: any, oldData: any, oldFilter: any) => any;
  /**
   * Transforms the raw data from the API /list endpoint before it is saved in react-query - mostly used if the data does not conform the {id: number, name: string} format
   */
  infiniteNormalizer?: (raw: PartialListDto<any>, t: TFunction) => PartialListDto<any>;
  /**
   * Setting for the react-query keepPreviousData option
   */
  keepPreviousData?: boolean;
  disableHotKeys?: boolean;
  /**
   * Indicates that the filter should take the customer filter from the global customer filter (appData?.selectedCustomer)
   */
  syncCustomerFilter?: boolean;
  goToDetail?: (id?: IdType) => void;
  goHome?: () => void;
  /**
   * Separator for the sorter - if the field is nested default is "."
   */
  sorterSeparator?: string;
}

export interface BaseDataType {
  id?: IdType;
  rowCount?: number;
  data?: any;
}

/**
 * Sort order options
 */
type SortOrder = "ascend" | "descend" | undefined;

/**
 * Pagination data
 */
export interface BaseFilter {
  /** Current page */
  page?: number;
  /** Page size */
  pageSize?: number;
  /** Current sort order */
  sortOrder?: SortOrder;
  /** Name of the field the data are sorted by */
  sortBy?: string;
  /** Optional full-text field */
  fulltextfield?: string;
  /** Fulltext search term */
  fulltext?: string;
  /**
   * Indicator that custom filter is used, it is set to true after the url filter is modified
   */
  f?: boolean;
  /**
   * tab
   */
  t?: boolean;
}

export function entityFactory(key: string): EntityConfigBase {
  return {
    baseKey: key,
    key: key + "_table",
    keyDetail: key + "_detail",
    baseRoute: key,
  };
}

/**
 * When entity used only for example tabs
 */
export const defaultEntityConfig = {
  key: "",
  baseKey: "",
  endpoint: undefined,
  roleView: [""],
  roleAdmin: [""],
  singularName: () => "",
  name: () => "",
  baseRoute: "",
  apiVersion: "v1",
};

interface UseCreateOneParams {
  customEndpoint?: string;
  apiVersion?: string;
  preventNotification?: boolean;
}

interface UseUpdateOneParams {
  customEndpoint?: string;
  apiVersion?: string;
  preventNotification?: boolean;
  preventInvalidation?: boolean;
  sendCulture?: boolean;
}

/**
 * Function that constructs functions
 * for entity react-query manipulation
 *
 * @param config - Entity configuration
 *
 * @returns Object with query functions
 */
/**
 * The hook's surface, named so the two public overloads below can share it.
 *
 * `useEntityImpl` carries the implementation; `useEntity` only chooses how `T` and
 * `TFilter` are arrived at.
 */
export type EntityHook<TPage, TDetail extends BaseDataType, TItem, TFilter> = ReturnType<
  typeof useEntityImpl<TPage, TDetail, TItem, TFilter>
>;

/**
 * Entity CRUD, filtering and cache management for one entityConf.
 *
 * Two ways to call it:
 *
 *     useEntity(entityConf)                  // T and TFilter inferred from the document
 *     useEntity<ContainerDto>(entityConf)    // T supplied by hand — the original form
 *
 * The inferring overload is the one to reach for. Supplying `T` is an *unchecked cast*:
 * nothing verifies it against what the endpoint actually returns, which is the same hole
 * that produced the wrong `toJson<T>` assertions found in §12–§18 of the migration.
 *
 * Both overloads exist because `config` used to be typed as `EntityConfig`, which widened
 * the literal `endpoint` that `defineEntity` preserves and made inference impossible. The
 * config type has to be the *first* type parameter for `T` to default from it — TypeScript
 * forbids a default that references a later parameter — so the two forms cannot be merged
 * into one signature. Explicitly passing `T` simply fails the `C extends EntityConfig`
 * constraint and falls through to the second overload.
 */
export default function useEntity<C extends EntityConfig>(
  config: C
): EntityHook<PageOf<C>, DetailOf<C>, ItemOf<ListOf<C>>, FilterOf<C>>;
export default function useEntity<T extends BaseDataType, TFilter = BaseFilter>(
  config: EntityConfig
): EntityHook<T, T, ItemOf<T>, TFilter>;
// The implementation signature is intentionally untyped in its return: it has to be
// assignable to both overloads, which disagree about where T comes from.
export default function useEntity(config: EntityConfig): any {
  return useEntityImpl(config);
}

function useEntityImpl<TPage, TDetail extends BaseDataType, TItem, TFilter = BaseFilter>(config: EntityConfig) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { resetDirty } = useFormDirty();

  function getQueryConf(enabled = true, initialData?: any) {
    return {
      enabled,
      refetchOnWindowFocus: config.disableCache,
      placeholderData: initialData,
      keepPreviousData: config.keepPreviousData,
    };
  }

  const navigate = useNavigate();
  const params = useParams();
  const { appData } = useApp();
  const selectedCustomer = appData?.selectedCustomer;
  const selectedCustomerId = selectedCustomer?.id;

  const { t, i18n } = useTranslation();

  function getUrlFilter(): BaseFilter & Partial<TFilter> {
    return qs.parse(location.search, {
      ignoreQueryPrefix: true,
      decoder: qsTypeDecoder(),
    }) as BaseFilter & Partial<TFilter>;
  }

  function getDetailEndpoint(id: IdType) {
    if (config.getDetailEndpoint) {
      return config.getDetailEndpoint(id);
    } else {
      return `${config.endpoint}/${id}`;
    }
  }

  /**
   * Gets query object for entity detail
   *
   * @param id             - Entity Id
   * @param enabled        - Is query enabled by default
   * @param apiVersion     - Optional API version - default = v1
   * @returns Query object as returned from @see useQuery
   */
  function useEntityDetailFetch(id: IdType, enabled = true, apiVersion?: string) {
    if (!config.keyDetail) {
      throw new Error("No key detail");
    }
    const endpoint = getDetailEndpoint(id);
    function dataFn() {
      if (id && id != 0) {
        const queryParams = config.sendCulture ? { culture: i18n.language } : {};
        const paramsEmpty = Object.keys(queryParams).length === 0;
        // A `getDetailEndpoint` override or a per-call apiVersion builds a URL the
        // generated operation cannot express, so those keep the old path.
        const op = config.getDetailEndpoint || apiVersion ? undefined : resolveOperations(config)?.detail;
        const request = op?.param
          ? (unwrap(
              op.call({ path: { [op.param]: id }, query: paramsEmpty ? undefined : queryParams })
            ) as Promise<TDetail>)
          : toJson<TDetail>(
              fetchEGate("GET", endpoint, undefined, paramsEmpty ? undefined : queryParams, {
                version: apiVersion || config.apiVersion,
              })
            );
        return request.catch((reason) => {
          if (reason?.status === 401) {
            navigate("/signin", { replace: true, state: location.pathname });
          }
          return Promise.reject(reason);
        });
      }
      return Promise.resolve({} as unknown as undefined);
    }

    return useQuery<TDetail | undefined, ApiError>({
      queryKey: [config.keyDetail, id],
      queryFn: () => dataFn(),
      ...getQueryConf(id != 0 && enabled),
    });
  }

  function useEntityFormFetch(id: IdType) {
    return useEntityDetailFetch(id, id != 0);
  }
  function getFilter(urlFilter?: BaseFilter & Partial<TFilter>) {
    if (!urlFilter?.f) {
      return {
        ...config.initialFilter,
        ...urlFilter,
      };
    }

    return urlFilter;
  }
  function useEntityFilter() {
    const urlFilter: BaseFilter & Partial<TFilter> = qs.parse(location.search, {
      ignoreQueryPrefix: true,
      decoder: qsTypeDecoder(),
    }) as BaseFilter & Partial<TFilter>;

    const filter = getFilter(urlFilter);

    /**
     * Callback for setting the newFilter
     * @param newFilterParam
     * @param userChanged the filter was changed by user
     */
    function setFilter(newFilterParam: Partial<TFilter> | Partial<BaseFilter>, userChanged = true) {
      const newPage = filter?.page === (newFilterParam as BaseFilter)?.page ? 1 : (newFilterParam as BaseFilter)?.page;
      const newFilter = { ...newFilterParam, page: newPage };
      startTransition(() => {
        navigate(
          `${location.pathname}?${qs.stringify({
            ...newFilter,
            f: userChanged,
            t: getTab(),
          })}`,
          { replace: true }
        );
      });
    }

    /**
     * Callback on table page or sort change
     */
    function onTableChange(
      pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: SorterResult<any> | SorterResult<any>[]
    ) {
      const singleSorter = sorter as SorterResult<ItemOf<TPage>>;
      const sorterSeparator = config?.sorterSeparator ?? ".";
      const tempSortBy = joinIfArray(singleSorter.field as never, sorterSeparator);
      const tempOrder = singleSorter.order as SortOrder;

      function getCurrentPage() {
        if (tempOrder !== undefined && (filter?.sortBy !== tempSortBy || tempOrder !== filter?.sortOrder)) {
          // If sorting changed, jump to first page
          return 1;
        }

        return pagination.current || 1;
      }

      setFilter({
        ...filter,
        page: getCurrentPage() as number,
        sortBy: tempOrder ? tempSortBy : undefined,
        sortOrder: tempOrder,
        pageSize: parseInt(pagination.pageSize as unknown as string, 10),
      });
    }

    function nextPage() {
      setFilter({
        ...filter,
        page: filter?.page ? (filter?.page as number) + 1 : 1,
      });
    }

    function prevPage() {
      if (filter?.page !== 1) {
        setFilter({
          ...(filter ?? {}),
          page: (filter?.page ?? 2) - 1,
        });
      }
    }

    function columnSorter(key: string) {
      return {
        sorter: true,
        sortOrder: filter?.sortBy === key ? filter?.sortOrder : undefined,
      };
    }

    function onSearch(fulltext: string) {
      if (filter) {
        setFilter({ ...filter, fulltext });
      }
    }

    function setFilterByKey(field: keyof TFilter, value: unknown) {
      if (filter) {
        const newFilter = {
          ...filter,
          [field]: value,
        };
        setFilter(newFilter);
      }
    }

    return {
      setFilter,
      setFilterByKey,
      onTableChange,
      columnSorter,
      onSearch,
      nextPage,
      prevPage,
      filter,
    };
  }

  function setData(data: TPage) {
    queryClient.setQueryData([config.key], data);
  }

  function setDetailData(data: TDetail) {
    queryClient.setQueryData([config.keyDetail, data.id], data);
  }

  function getData() {
    const queryKeys = queryClient
      .getQueryCache()
      .getAll()
      .map((query) => query.queryKey);
    const queryKeysFilered = queryKeys.filter((key) => key.includes(config.key));
    const queryKey = queryKeysFilered[queryKeysFilered.length - 2];
    return queryClient.getQueryData(queryKey as string[]);
  }

  async function getPagedData(
    filter?: TFilter,
    args?: {
      ac?: AbortController;
      version?: string;
    }
  ) {
    function formatFilter() {
      function addCulture() {
        if (config.sendCulture) {
          return {
            ...filter,
            culture: i18n.language,
          };
        }
        return filter;
      }
      const f = addCulture();
      if (config.filterFormatter && f) {
        return config.filterFormatter(f);
      }
      return f;
    }

    // A per-call version override points at a different base path than the config
    // describes, so it cannot use the config's operation either.
    const op = args?.version ? undefined : resolveOperations(config)?.page;
    const request = op
      ? (unwrap(op.call({ query: formatFilter(), signal: args?.ac?.signal })) as Promise<TPage>).catch((reason) => {
          if (reason?.status === 401) {
            navigate("/signin", { replace: true, state: location.pathname });
          }
          // Rejecting with `undefined` rather than the error is what `toJson` did on
          // this path — the catch above swallowed the reason and `toJson` then rejected
          // with the resolved `undefined`. Preserved deliberately; see plan §20.
          return Promise.reject(undefined);
        })
      : toJson<TPage>(
          fetchEGate("GET", config.endpoint + "/", undefined, formatFilter(), {
            ...args,
            version: args?.version || config.apiVersion,
          }).catch((reason) => {
            if (reason.status === 401) {
              navigate("/signin", { replace: true, state: location.pathname });
            }
          })
        );
    return request.then((res) => {
      if (config.normalizer) {
        return config.normalizer(res, getData(), filter);
      }
      return res;
    });
  }

  /**
   * Get the key filter for the current filter
   * @param filter
   */
  function getKeyFilter(filter: any) {
    const { t: __, f, ...restFilter } = (filter as BaseFilter) ?? ({} as BaseFilter);

    if (!f) {
      if (selectedCustomer) {
        return {
          ...restFilter,
          ...config.initialFilter,
          selectedCustomer,
        };
      }
      if (Object.keys(restFilter).length === 0) {
        return undefined;
      }
      return { ...restFilter, ...config.initialFilter };
    }

    if (selectedCustomer) {
      return {
        ...restFilter,
        selectedCustomer,
      };
    }
    if (Object.keys(restFilter).length === 0) {
      return undefined;
    }
    return restFilter;
  }

  function useEntityFetch(
    { key }: EntityConfig,
    filter?: TFilter,
    args?: {
      enabled?: boolean;
      version?: string;
    }
  ) {
    const enabled = args?.enabled ?? true;
    function get(): any {
      function getRequestFilter(): any {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { t: __, f } = (filter as BaseFilter) ?? ({} as BaseFilter);

        if (!f) {
          if (selectedCustomerId) {
            return {
              customerId: selectedCustomerId,

              ...config.initialFilter,
              ...filter,
            };
          }
          return { ...config.initialFilter, ...filter };
        }
        if (selectedCustomerId) {
          return {
            customerId: selectedCustomerId,
            ...filter,
          };
        }
        return filter;
      }
      if ((config.shouldFetch !== undefined && !config?.shouldFetch?.(filter)) || !enabled) {
        return getData();
      }

      return getPagedData(getRequestFilter(), args);
    }
    const [debouncedFilter] = useDebounce(filter, 500, {
      leading: true,
      equalityFn: (a, b) => JSON.stringify(a) === JSON.stringify(b),
    });

    return useQuery<TPage, ApiError>({
      queryKey: [key, getKeyFilter(debouncedFilter)],
      queryFn: get,
      ...getQueryConf(enabled, config.initialData),
    });
  }

  function useEntityInfiniteFetch(filter?: TFilter) {
    return useQuery({
      queryKey: [config.key + "_list", filter],
      queryFn: () => infiniteQuery(filter),
    });
  }

  function invalidateDependencies() {
    if (config.dependencies) {
      for (const dependency of config.dependencies as string[]) {
        invalidateEntity(dependency);
      }
    }
  }

  function invalidateEntity(key: string) {
    void queryClient.invalidateQueries({
      predicate: (query) => {
        return queryKeyMatchesEntity(query.queryKey, key);
      },
    });
  }

  /**
   * Invalidate everything cached for *this* entity — both conventions.
   *
   * `invalidateEntity` only reaches queries `useEntity` named itself
   * (`["container_detail", 7]`). Anything fetched through the generated react-query helpers
   * in `src/api/queries` is keyed by operation id and is invisible to it, so a mutation here
   * would leave those stale forever — the app sets `staleTime: Infinity`, so nothing else
   * would ever refresh them.
   *
   * Only this entity's own operations are matched. Dependencies are named by string
   * (`dependencies: ["container"]`) with no way to reach the other entity's operations from
   * here, so they stay string-only, exactly as before.
   */
  function invalidateThisEntity() {
    invalidateEntity(config.baseKey);
    const operations = resolveOperations(config);
    if (!operations) return;
    const ids = new Set(
      Object.values(operations)
        .map((op) => (typeof op === "object" && op !== null && "id" in op ? (op as { id: string }).id : undefined))
        .filter((id): id is string => typeof id === "string")
    );
    if (!ids.size) return;
    void queryClient.invalidateQueries({
      predicate: (query) => queryKeyMatchesOperations(query.queryKey, ids),
    });
  }

  function useDeleteOne() {
    const func = (id: string | number | undefined) => {
      // Guard rather than requesting `/endpoint/undefined`.
      if (id === undefined) return Promise.reject(new Error("Cannot delete an entity without an id"));
      const op = resolveOperations(config)?.remove;
      if (op?.param) return unwrap(op.call({ path: { [op.param]: id } }));
      return fetchEGate("DELETE", `${config.endpoint}/${id}`, undefined, undefined, {
        version: config.apiVersion,
      });
    };
    return useMutation({
      mutationFn: func,
      onSuccess: () => {
        trackEvent("entity.delete", { entity: config.baseKey });
      },
      onSettled: () => {
        void queryClient.invalidateQueries({ queryKey: [config.key] });
        invalidateThisEntity();
        invalidateDependencies();
      },
      onError: (err, newTodo, context: any) => {
        trackError("entity.error", { entity: config.baseKey, action: "delete" });
        Modal.error({
          content: <div>{config.singularName(t) + t(" deletion cannot be completed.")}</div>,
        });

        const filterKey = getKeyFilter(getUrlFilter());

        queryClient.setQueryData([config.key, getKeyFilter(filterKey)], context.prev);
      },
      onMutate: (id) => {
        const filterKey = getKeyFilter(getUrlFilter());

        void queryClient.cancelQueries({
          queryKey: [config.keyDetail],
        });
        void queryClient.cancelQueries({
          queryKey: [config.key, filterKey],
        });

        const queryKey = [config.key, filterKey].filter((i) => i);
        // This is the *page* query's cache entry, so it holds TPage. It used to read
        // `PagedResultDto<T>`, contradicting the `useQuery<T & BaseDataType>` a few lines
        // above for the very same key — the mismatch was invisible because the only use
        // is through a `(old: any)` setQueryData callback.
        const prev: TPage | undefined = queryClient.getQueryData(queryKey);
        if (prev) {
          queryClient.setQueryData(queryKey, (old: any) => ({
            ...old,
            results: [...old.results].filter((o) => o.id !== id),
          }));
        }

        return { prev };
      },
    });
  }

  function useActivateOne() {
    const func = (id: string | number | undefined) => {
      if (id === undefined) return Promise.reject(new Error("Cannot activate an entity without an id"));
      const op = resolveOperations(config)?.activate;
      if (op?.param) return unwrap(op.call({ path: { [op.param]: id } }));
      return fetchEGate("PUT", `${config.endpoint}/${id}/activate`, undefined, undefined, {
        version: config?.apiVersion,
      });
    };
    return useMutation({
      mutationFn: func,
      onSuccess: () => {
        trackEvent("entity.activate", { entity: config.baseKey });
      },
      onSettled: () => invalidateThisEntity(),
    });
  }

  function useUpdateOne<TUpdate extends BaseDataType>(form?: FormInstance, updateOneParams?: UseUpdateOneParams) {
    function getEndpoint(id: IdType) {
      if (config.getDetailEndpoint) {
        return config.getDetailEndpoint(id);
      }
      if (updateOneParams?.customEndpoint) {
        return updateOneParams.customEndpoint;
      }
      return config.endpoint as string;
    }
    function getUrl(id?: IdType) {
      const endpoint = getEndpoint(id as IdType);
      if (config.noIdInCall) {
        return endpoint;
      } else if (id) {
        return `${endpoint}/${id as string}`;
      }
      return endpoint;
    }

    const func = (data: TUpdate) => {
      if (!config.endpoint) {
        return Promise.reject("No endpoint");
      }
      // Every one of these builds a URL the operation cannot express: a detail-endpoint
      // override, a custom endpoint, a per-call version, `noIdInCall` (PUT to the
      // collection), or simply not having an id yet.
      const op =
        config.getDetailEndpoint || updateOneParams?.customEndpoint || updateOneParams?.apiVersion || config.noIdInCall
          ? undefined
          : resolveOperations(config)?.update;
      const query = updateOneParams?.sendCulture ? { culture: i18n.language } : undefined;
      if (op?.param && data.id) {
        return unwrap(op.call({ path: { [op.param]: data.id }, query, body: data, ...silent })) as Promise<TUpdate>;
      }
      return toJson<TUpdate>(
        fetchEGate("PUT", getUrl(data.id) as string, data, query, {
          showNotification: false,
          version: updateOneParams?.apiVersion || config.apiVersion,
        })
      );
    };

    return useMutation({
      mutationFn: func,
      onSuccess: (data: TUpdate) => {
        trackEvent("entity.update", { entity: config.baseKey });
        if (form) {
          resetDirty();
        }
        if (!config.noRedirect) {
          if (data?.id && !config.noIdInUrl) {
            navigate(withReturnTo(`/app/${config?.baseRoute}/${data?.id as string}`));
          } else if (config.noIdInUrl) {
            navigate(`/app/${config?.baseRoute}`);
          }
        }
        if (!updateOneParams?.preventNotification) {
          notification.success({
            message: `${t("Update successful")}`,
            placement: "top",
          });
        }

        if (!updateOneParams?.preventInvalidation) {
          invalidateThisEntity();
          invalidateDependencies();
        }
      },
      onError: (error: ApiError) => {
        trackError("entity.error", { entity: config.baseKey, action: "update" });
        responseErrorNotification(error, t);

        form?.setFields(apiErrorsToFieldErrors(error));
      },
    });
  }

  function useCreateOne(form?: FormInstance, createOneParams?: UseCreateOneParams | undefined) {
    const func = (data: any) => {
      if (!config.endpoint) {
        return Promise.reject("No endpoint");
      }
      function getEndpoint() {
        if (createOneParams?.customEndpoint) {
          return createOneParams?.customEndpoint as string;
        }
        return config.endpoint as string;
      }
      const op = createOneParams?.customEndpoint || createOneParams?.apiVersion ? undefined : resolveOperations(config)?.create;
      if (op) {
        return unwrap(op.call({ body: data, ...silent })) as Promise<TDetail>;
      }
      return toJson<TDetail>(
        fetchEGate("POST", getEndpoint(), data, undefined, {
          showNotification: false,
          version: createOneParams?.apiVersion || config.apiVersion,
        })
      );
    };

    return useMutation({
      mutationFn: func,
      onSuccess: (data: TDetail) => {
        trackEvent("entity.create", { entity: config.baseKey });
        if (form) {
          resetDirty();
        }
        if (!config.noRedirect) {
          if (data?.id) {
            navigate(withReturnTo(`/app/${config.baseRoute}/${data?.id as string}`));
          }
        }
        if (!createOneParams?.preventNotification) {
          notification.success({
            message: t("Create successful"),
            placement: "top",
          });
        }

        invalidateDependencies();
        invalidateThisEntity();
      },
      onError: (error: ApiError) => {
        trackError("entity.error", { entity: config.baseKey, action: "create" });
        responseErrorNotification(error, t);
        form?.setFields(apiErrorsToFieldErrors(error));
      },
    });
  }

  function getExportUrl() {
    return config.endpoint + "/export.csv";
  }

  function goHome() {
    if (config.goHome) {
      config.goHome();
      return;
    }
    navigate(`/app/${config.baseRoute}`);
  }

  /**
   * The returnTo of the current url, set when the user did not come from the list of this entity
   * (e.g. from one of the tables in the container tabs).
   */
  function getReturnTo() {
    return new URLSearchParams(location.search).get("returnTo") ?? undefined;
  }

  /**
   * Carries an inherited returnTo over to the next url.
   *
   * Without it the back button falls back to the list of this entity as soon as the user moves between the
   * detail and the edit screen, which is wrong when they came from somewhere else entirely.
   */
  function withReturnTo(url: string) {
    const returnTo = getReturnTo();
    if (!returnTo) {
      return url;
    }
    return `${url}${url.includes("?") ? "&" : "?"}returnTo=${encodeURIComponent(returnTo)}`;
  }

  function goToEdit(id?: IdType) {
    const tabQs = getTabQueryString();
    startTransition(() => {
      if (id) {
        navigate(withReturnTo(`/app/${config.baseRoute}/${id}/edit${tabQs}`));
      } else {
        navigate(withReturnTo(`/app/${config.baseRoute}/edit${tabQs}`));
      }
    });
  }

  function goToNew() {
    const listUrl = `/app/${config.baseRoute}`;
    const newUrl = `${listUrl}/0/edit`;
    // Opened from anywhere but this entity's list (e.g. the customer tree), Cancel has to return there.
    const url =
      location.pathname === listUrl
        ? newUrl
        : `${newUrl}?returnTo=${encodeURIComponent(location.pathname + location.search)}`;
    startTransition(() => {
      navigate(url);
    });
  }

  function goToDetail(id: IdType) {
    if (config.goToDetail) {
      config.goToDetail(id);
      return;
    }
    // empty url
    const url = new URL("https://e.e");

    const newUrl = "/app/" + config.baseRoute;
    const currentTab = getTab();
    if (currentTab) {
      url.searchParams.set("t", currentTab);
    }
    // An inherited returnTo wins - it points at where the user actually came from (e.g. a container tab),
    // while the fallback assumes they came from the list of this entity.
    url.searchParams.set("returnTo", getReturnTo() ?? newUrl + location.search);
    startTransition(() => {
      navigate(`/app/${config.baseRoute}/${id}/${url.search}`);
    });
  }

  function setActiveTab(key: string) {
    if (key === "edit") {
      return;
    }

    searchParams.set("t", key);
    startTransition(() => {
      setSearchParams(searchParams);
    });
  }

  function getTab() {
    return searchParams.get("t");
  }

  function getTabQueryString() {
    const tab = getTab();
    if (tab) {
      return `?t=${tab}`;
    }
    return "";
  }

  function getActiveTab(defaultTab = "1"): string {
    const tab = getTab();
    if (tab) {
      return `${tab}`;
    }
    return defaultTab;
  }

  function isEdit() {
    return location.pathname.includes("edit");
  }

  useHotkeys("f2", () => {
    if (config.disableHotKeys) return;
    // @ts-ignore
    const id = params.id;
    if (id) {
      if (isEdit()) {
        goToDetail(id);
      } else {
        goToEdit(id);
      }
    }
  });

  useHotkeys("r", () => {
    if (config.disableHotKeys) return;

    invalidateThisEntity();
  });

  /**
   * Gets title for entity detail view
   * @param isNew - Indicates new entity
   * @param title - Optional override for the title
   * @returns Title to show on the form
   */
  function getFormTitle(isNew: boolean, title?: string) {
    return isNew ? `${t("New")} ${config.singularName(t)}` : `${title ?? config.singularName(t)}`;
  }

  function addInitial(value: TItem[], initial: TItem): TItem[] {
    if (initial) {
      if (isArray(initial)) {
        return unionBy(initial as unknown as any[], value, "id");
      } else {
        return unionBy([initial], value, "id");
      }
    }
    return value;
  }

  async function infiniteQuery(filter: any, initialValue?: any, ac?: AbortController): Promise<PartialListDto<TItem>> {
    if (!config.endpoint) {
      return Promise.reject("No endpoint");
    }
    function getListEndpoint() {
      if (config.listEndpoint) {
        return config.listEndpoint;
      }
      return config.endpoint + "/list";
    }
    // `listEndpoint` overrides the whole URL — several configs point it at the page
    // route rather than `/list` — so it keeps the old path.
    const op = config.listEndpoint ? undefined : resolveOperations(config)?.list;
    const json = op
      ? ((await unwrap(op.call({ query: { ...filter }, signal: ac?.signal }))) as PartialListDto<TItem>)
      : await toJson<PartialListDto<TItem>>(
          fetchEGate("GET", `${getListEndpoint()}`, null, { ...filter }, { ac, version: config.apiVersion })
        );

    function getResponseData() {
      if (config.infiniteNormalizer) {
        return config.infiniteNormalizer(json, t);
      }
      return json;
    }
    const responseData = getResponseData();
    if (initialValue) {
      const initialLength = isArray(initialValue) ? initialValue.length : 1;

      return {
        results: addInitial(responseData.results, initialValue),
        first: 1,
        rowCount: initialValue ? responseData.rowCount + initialLength : responseData.rowCount,
      };
    }
    return responseData;
  }

  async function isValueUsed(
    name: string,
    value: any,
    fieldTitle?: string,
    message?: string,
    scope?: Record<keyof any, unknown>
  ) {
    if (!config.endpoint) {
      return undefined;
    }
    return baseIsValueUsed(
      resolveOperations(config)?.valueUsed ?? (config.endpoint as string),
      name,
      value,
      message ? message : `${fieldTitle} ${t("has already been used")}`,
      scope,
      config.apiVersion
    );
  }

  /**
   * Gets an entity
   * @param id
   */
  async function getOne(id: number): Promise<TDetail> {
    // Note this deliberately ignores `getDetailEndpoint`, as it always has — it builds
    // `${endpoint}/${id}` directly, which is exactly what the detail operation resolves to.
    const op = resolveOperations(config)?.detail;
    if (op?.param) return unwrap(op.call({ path: { [op.param]: id } })) as Promise<TDetail>;
    return toJson<TDetail>(
      fetchEGate("GET", `${config.endpoint}/${id}`, undefined, undefined, {
        version: config.apiVersion,
      })
    );
  }

  function getDetailLink(id: IdType | undefined) {
    return `/app/${config.baseRoute}/${id}`;
  }

  return {
    useEntityFetch: (
      filter?: (TFilter & BaseFilter) | undefined,
      args?: {
        enabled?: boolean;
        version?: string;
      }
    ) => useEntityFetch(config, filter, args),
    useEntityDetailFetch: (id: IdType) => useEntityDetailFetch(id),
    useEntityFormFetch: (id: IdType) => useEntityFormFetch(id),
    useEntityInfiniteFetch,
    useEntityFilter: () => useEntityFilter(),
    useEntityDeleteOne: useDeleteOne,
    useEntityActivateOne: useActivateOne,
    useEntityCreateOne: useCreateOne,
    useEntityUpdateOne: useUpdateOne,
    goToEdit: (id?: IdType) => goToEdit(id),
    goToNew: () => goToNew(),
    goToDetail: (id: IdType) => goToDetail(id),
    goHome: () => goHome(),
    setData: (data: TPage) => setData(data),
    invalidateEntity: () => invalidateThisEntity(),
    setActiveTab,
    getActiveTab,
    getExportUrl,
    getFormTitle,
    infiniteQuery,
    isValueUsed,
    setDetailData,
    getOne,
    getPagedData,
    getDetailLink,
  };
}
